// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
// Overview tab: screenshot hero + about card, system map graph, and recent
// deployments widget. RecentDeployments is private to this module — the
// widget is only used here.
import * as React from 'react';
import { Link } from 'react-router-dom';

import { MonoValue } from '@/components/greenlight/MonoValue';
import { StatusBadge } from '@/components/greenlight/StatusBadge';
import { RefreshWarning } from '@/components/greenlight/RefreshWarning';
import { AD_CARD_HD, AD_GROUP, AD_ROW_INT } from '@/components/greenlight/surfaces';
import { pipelineMeta } from '@/components/greenlight/appStatusMeta';
import { relativeTime } from '@/components/greenlight/relativeTime';
import { WorkloadsResourcesGraph } from '@/components/app-detail/WorkloadsResourcesGraph';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import { isTransientQueryError, liveQueryOptions, statusPollInterval } from '@/lib/polling';
import type { z } from 'zod';
import type { GetAppResultSchema } from '@greenlight/shared';
import { AboutCard } from './AboutCard';
import { ScreenshotHero } from './ScreenshotHero';
import { TabErrorPanel, TabLoadingRows } from './tab-primitives';

type GetAppResult = z.infer<typeof GetAppResultSchema>;

// ── Recent deployments (private widget used only by OverviewTab) ──────────────

function RecentDeployments({ appId }: { appId: string }): React.JSX.Element {
  // Distinct key from PipelineTab's full-list query — same ['runs', appId] with
  // different params would collide in the react-query cache.
  const { data, isLoading, isError, error } = api.pipeline.listRuns.useQuery(
    ['runs', appId, { limit: 5 }],
    {
      params: { id: appId },
      query: { limit: 5 },
    },
    {
      ...liveQueryOptions,
      queryKey: ['runs', appId, { limit: 5 }],
      refetchInterval: ({ state }) =>
        statusPollInterval(state.data?.body.items.map((run) => run.status) ?? []),
    },
  );

  const header = (
    <>
      <div className={AD_CARD_HD}>
        <span className="text-sm font-semibold tracking-[-0.01em]">Recent deployments</span>
      </div>
      {isError && data?.status === 200 && isTransientQueryError(error) && <RefreshWarning />}
    </>
  );

  if (isLoading)
    return (
      <div className={AD_GROUP} data-testid="recent-deployments">
        {header}
        <div className="p-4">
          <TabLoadingRows />
        </div>
      </div>
    );
  if ((isError && !isTransientQueryError(error)) || !data || data.status !== 200)
    return (
      <div className={AD_GROUP} data-testid="recent-deployments">
        {header}
        <div className="p-4">
          <TabErrorPanel message="Couldn't load deployments." />
        </div>
      </div>
    );

  // Every recent run, whatever its status — a failing app is exactly when the
  // owner opens Overview, and hiding non-passed runs hid that signal (docs/28
  // specs no successful-only filter). Each row carries its status badge.
  const runs = data.body.items.slice(0, 5);
  if (!runs.length)
    return (
      <div className={AD_GROUP} data-testid="recent-deployments">
        {header}
        <div className="p-4 text-sm text-muted-foreground">No deployments yet.</div>
      </div>
    );

  return (
    <div className={AD_GROUP} data-testid="recent-deployments">
      {header}
      <ul>
        {runs.map((r) => {
          const meta = pipelineMeta(r.status);
          return (
            <li key={r.id} className={AD_ROW_INT}>
              <Link
                to={`/apps/${appId}/runs/${r.id}`}
                className="flex flex-1 items-center gap-3.5 no-underline"
                data-testid={`recent-run-${r.id}`}
              >
                <MonoValue>{r.commitSha.slice(0, 7)}</MonoValue>
                <StatusBadge variant={meta.variant}>{meta.label}</StatusBadge>
                <span className="ml-auto text-xs text-muted-foreground" title={r.triggeredAt}>
                  {relativeTime(r.triggeredAt)}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ── OverviewTab ───────────────────────────────────────────────────────────────

interface OverviewTabProps {
  detail: GetAppResult;
  appId: string;
  /** Screenshot URLs from app; empty/undefined = empty state. */
  screenshotUrls?: string[] | null;
  appUrl?: string | null;
  docSummary?: string | null;
  docPurpose?: string | null;
  docArchitecture?: string | null;
  description?: string | null;
}

export function OverviewTab({
  detail,
  appId,
  screenshotUrls,
  appUrl,
  docSummary,
  docPurpose,
  docArchitecture,
  description,
}: OverviewTabProps): React.JSX.Element {
  const heroVisible = (screenshotUrls?.length ?? 0) > 0;
  return (
    <div className="space-y-4" data-testid="overview-tab">
      {/* Row 1: ScreenshotHero (2fr) + AboutCard (3fr) */}
      <div
        className={cn(
          'grid items-start gap-4',
          heroVisible ? 'lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]' : undefined,
        )}
      >
        {heroVisible && (
          <ScreenshotHero
            urls={screenshotUrls ?? undefined}
            appName={detail.app.name}
            slug={detail.app.slug}
            appUrl={appUrl}
          />
        )}
        <AboutCard
          summary={docSummary ?? null}
          purpose={docPurpose ?? null}
          architecture={docArchitecture ?? null}
          description={description ?? null}
        />
      </div>
      {/* Row 2: WorkloadsResourcesGraph (3fr) + Recent deployments (2fr) */}
      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        {/* System map — the graph component renders its own card + header. */}
        <div data-testid="system-map-card">
          <WorkloadsResourcesGraph detail={detail} />
        </div>
        <RecentDeployments appId={appId} />
      </div>
    </div>
  );
}
